import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@structural-climbing/ui'
import type { ChildSummary } from './useMyChildren'
import { personColor } from './personColor'

interface Props {
  childList: ChildSummary[]
  /** Child id → when they checked in, for the children inside right now. */
  childCheckIns: Record<string, string>
  gymSlug: string
  gymTimezone: string | null
  onShowCode: (childId: string) => void
  onCheckOut?: (childId: string) => Promise<void>
  /**
   * `row` swipes sideways (one line of the page); `list` stacks full-width rows
   * for Home's Familie tab, where the children have the screen to themselves.
   */
  layout?: 'row' | 'list'
}

/**
 * Your children, as a parent needs them at the desk: who is inside, whose code
 * to show, and a way to check them out.
 *
 * Everything else the Family card did (adding, editing, removing a child,
 * giving them a login) lives in Contul meu › Familie, behind "Manage".
 * A member without children sees nothing here at all.
 */
export function FamilyStrip({
  childList,
  childCheckIns,
  gymSlug,
  gymTimezone,
  onShowCode,
  onCheckOut,
  layout = 'row',
}: Props) {
  const list = layout === 'list'
  const { t } = useTranslation()
  const [checkOutTarget, setCheckOutTarget] = useState<ChildSummary | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  if (childList.length === 0) return null

  const time = (iso: string) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: gymTimezone ?? undefined,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))

  const confirmCheckOut = async () => {
    if (!checkOutTarget || !onCheckOut) return
    setBusy(true)
    setFailed(false)
    try {
      await onCheckOut(checkOutTarget.id)
      setCheckOutTarget(null)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="flex flex-col gap-2" aria-label={t('family.yourChildren')}>
      <div className="flex items-baseline justify-between gap-2 px-1">
        <p className="eyebrow text-foreground/40">{t('family.yourChildren')}</p>
        <Link to={`/${gymSlug}/family`} className="text-xs font-semibold text-[var(--gym-primary)]">
          {t('family.manage')} ›
        </Link>
      </div>

      {/* One row you swipe through, so three children cost the page one line. */}
      <div
        role="list"
        className={
          list ? 'flex flex-col gap-2' : 'scrollbar-hide -mx-4 flex gap-2 overflow-x-auto px-4 pb-1'
        }
      >
        {childList.map((child) => {
          const sub = child.active_subscription
          const inside = childCheckIns[child.id]
          // Only a plan that has started opens the door, as in the Family card.
          const canEnter = sub?.effective_status === 'active'
          const status = inside
            ? t('checkin.checkedInAt', { time: time(inside) })
            : canEnter && sub
              ? sub.entries_remaining !== null
                ? t('family.entriesRemaining', { count: sub.entries_remaining })
                : sub.gym_plans.name
              : t('family.noPlanShort')

          return (
            <div
              role="listitem"
              key={child.id}
              className={`flex ${list ? 'w-full py-3' : 'w-[min(15rem,75vw)] py-2'} shrink-0 items-center gap-2.5 rounded-lg border bg-content1 pl-2.5 pr-1.5 shadow-e1 ${
                inside ? 'border-success/40' : 'border-[var(--gym-line)]'
              }`}
            >
              <span
                className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                style={{ backgroundColor: personColor(child.id) }}
              >
                {child.first_name.charAt(0).toUpperCase()}
                {inside && (
                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-content1 bg-success" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-semibold leading-tight">
                  {child.first_name}
                </span>
                <span
                  className={`block truncate text-xs ${
                    inside ? 'text-success' : canEnter ? 'text-foreground/50' : 'text-danger'
                  }`}
                >
                  {status}
                </span>
              </span>
              {inside && onCheckOut ? (
                <Button
                  size="sm"
                  variant="flat"
                  className="shrink-0"
                  onPress={() => setCheckOutTarget(child)}
                >
                  {t('checkin.checkOutShort')}
                </Button>
              ) : canEnter ? (
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label={t('family.showCodeFor', { name: child.first_name })}
                  onPress={() => onShowCode(child.id)}
                  className="shrink-0 text-foreground"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path d="M3 3h7v7H3zm2 2v3h3V5zM14 3h7v7h-7zm2 2v3h3V5zM3 14h7v7H3zm2 2v3h3v-3zM14 14h3v3h-3zm4 0h3v3h-3zm-4 4h3v3h-3zm4 0h3v3h-3z" />
                  </svg>
                </Button>
              ) : null}
            </div>
          )
        })}
      </div>

      <Modal
        isOpen={checkOutTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCheckOutTarget(null)
        }}
      >
        <ModalContent>
          <ModalHeader>{t('checkin.checkOutConfirmTitle')}</ModalHeader>
          <ModalBody>
            <p className="text-sm text-foreground/60">
              {t('checkin.checkOutConfirmMessageSimple')}
            </p>
            {failed && <p className="text-xs text-danger">{t('checkin.checkOutError')}</p>}
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={() => setCheckOutTarget(null)} isDisabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button color="danger" isLoading={busy} onPress={() => void confirmCheckOut()}>
              {t('checkin.checkOutConfirmButton')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </section>
  )
}
